import { useEffect, useRef } from 'react';
import { useIsTopmostLayerCheck, useModalStackDepth } from '@/contexts/ModalStackContext';

/**
 * Open popups that own Escape themselves (Radix popper content, listboxes, menus).
 * If the key event originates from inside one of these, the popup handles it and the surrounding layer must stay open.
 */
const OPEN_POPUP_SELECTOR =
  '[data-radix-popper-content-wrapper], [role="listbox"], [role="menu"]';

/**
 * Focus sits on the OWNER of an open popup: an expanded combobox, or an expanded trigger that declares a popup
 * (`aria-haspopup`). A plain disclosure/accordion toggle (`aria-expanded` without `aria-haspopup`) is NOT a popup
 * and must not swallow Escape. `aria-haspopup="dialog"` triggers are excluded too: a dialog is its own Radix layer and
 * handles Escape itself, so its trigger (which can still hold focus, e.g. the Award card) must never veto a close. A custom popup trigger only opts in (aria-haspopup) if its popup closes on Esc itself.
 */
const OPEN_POPUP_OWNER_SELECTOR =
  '[role="combobox"][aria-expanded="true"], [aria-expanded="true"][aria-haspopup]:not([aria-haspopup="false"]):not([aria-haspopup="dialog"])';

export const isInsideOpenPopup = (node: EventTarget | null): boolean =>
  node instanceof Element &&
  (node.closest(OPEN_POPUP_SELECTOR) !== null || node.closest(OPEN_POPUP_OWNER_SELECTOR) !== null);

/**
 * For Radix Dialog-level `onEscapeKeyDown` handlers. True when this Escape was already consumed by a child layer
 * (Radix Select/Popover preventDefault the key and unregister themselves synchronously, so by the time the Dialog
 * runs the popup is gone from the DOM) or still belongs to an open inline popup. The caller must then
 * `preventDefault()` and keep its container open. A plain Escape with nothing open is not prevented.
 */
export const shouldYieldEscape = (event: KeyboardEvent): boolean =>
  event.defaultPrevented || isInsideOpenPopup(event.target) || isInsideOpenPopup(document.activeElement);

/**
 * useEscapeLayer
 *
 * Registers a non-Radix drawer/panel as a layer in the modal stack and closes it on Escape, but ONLY when it
 * is the topmost layer and the key was not already consumed by an inner popover/listbox/select
 * (one Esc = one layer). Inner popups consume the key by calling `preventDefault()` (Radix does this itself,
 * in the capture phase); this listener runs in the bubble phase so it always sees that.
 *
 * @returns the layer's stack depth (feed it to `getStackZIndex` when the panel should sit in the stack).
 */
export function useEscapeLayer(isOpen: boolean, onClose: () => void): number {
  const depth = useModalStackDepth(isOpen);
  const isTopmost = useIsTopmostLayerCheck();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const depthRef = useRef(depth);
  depthRef.current = depth;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
      if (!isTopmost(depthRef.current)) return;
      if (isInsideOpenPopup(e.target) || isInsideOpenPopup(document.activeElement)) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isTopmost]);

  return depth;
}
